/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import React from 'react';
import { Button, Header as BaseHeader, Modal, Dimmer, Loader, Dropdown } from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import { makeObservable, observable, action, runInAction } from 'mobx';
import FileSaver from 'file-saver';
import { displayError } from '../../helpers/notification';
import { getReviewRequestAccessForm } from '../../models/forms/ReviewRequestAccessForm';
import { getOptionsFromRules } from '../../helpers/utils';
import Form from '../helpers/fields/Form';
import Toggle from '../helpers/fields/Toggle';
import DateField from '../helpers/fields/DateField';
import TextArea from '../helpers/fields/TextArea';
import Radio from '../helpers/fields/Radio';
import Header from '../helpers/fields/Header';
import StudyCollectionsCheckbox from '../studies/StudyCollectionsCheckbox';
import ByWithProfile from '../helpers/ByWithProfile';

class ReviewAccessRequest extends React.Component {
  modalOpen = false;

  constructor(props) {
    super(props);
	this.decision = '';
    makeObservable(this, {
	  decision: observable,
      modalOpen: observable,
    });
    this.accessRequest = props.accessRequest;
    this.cleanModal = this.cleanModal.bind(this);
    this.cleanModal();
    this.state = {
      isProcessing: false,
    };
  }

  cleanModal() {
    runInAction(() => {
      this.modalOpen = false;
    });
  }

  handleRevokePermission = async () => {
    this.setState({ isProcessing: true });
    const updateRequest = { ...this.accessRequest };
    await this.props.onRevokeAccess(this.props.studyId, updateRequest);
    this.setState({ isProcessing: false });
  };

  handleFormCancel = form => {
    form.clear();
    this.cleanModal();
  };

  handleFormError = (_form, _errors) => {};

  handleFormSubmission = async form => {
    try {
      const requestValues = form.values();

      // don't allow if expiry date is earlier than or equal to today	    
      if (typeof requestValues.expiry !== 'undefined' && requestValues.expiry.length) {
	  const expiryDate = new Date(requestValues.expiry).getTime();
	  const todayDate = new Date().getTime();
	  if (expiryDate <= todayDate) {
              displayError('Please indicate a future date for Expiry Date!');
	      return;
	  }
      }

      // don't allow empty collection list if permission type is collection
      if (requestValues.permissionType === 'collection' && requestValues.collectionIds.length === 0) {
        displayError('Please indicate collections for collection permission type!');
        return;
      }

      const updateRequest = { ...this.accessRequest, ...requestValues };
      if (requestValues.permissionType === 'study') {
        delete updateRequest.collectionIds;
      }
      if (requestValues.permissionType === 'collection') {
        updateRequest.isMetaData = false;
        updateRequest.isUpload = false;
      }

      if (typeof updateRequest.selectedRetrieval === 'undefined' || updateRequest.selectedRetrieval.length === 0) {
          // this is a not an archived study, retrieval type is not required
          delete updateRequest.selectedRetrieval;
      }
	    

	  if(this.decision === 'approve'){
		await this.props.onGrantAccess(this.props.studyId, updateRequest);
	  }else if (this.decision === 'deny'){
		await this.props.onDenyAccess(this.props.studyId, updateRequest);
	  } else {
		displayError('No decision made!');
	  }
      form.clear();
      this.cleanModal();
    } catch (error) {
      // displayError(error);
    }
  };
  
  render() {
    return (
      <Modal closeIcon trigger={this.renderTrigger()} open={this.modalOpen} onClose={this.cleanModal}>
        <Dimmer.Dimmable dimmed={this.state.isProcessing}>
          <Dimmer active={this.state.isProcessing} inverted>
            <Loader size="big" />
          </Dimmer>
          <div className="mt2 animated fadeIn">
            <BaseHeader as="h3" icon textAlign="center" className="mt3" color="grey">
              Review Request
            </BaseHeader>
            <div className="mx3 animated fadeIn">{this.renderReviewAccessRequestForm()}</div>
          </div>
        </Dimmer.Dimmable>
      </Modal>
    );
  }

  renderTrigger() {
    const props = this.props.buttonProps || {
      floated: 'right',
      color: 'blue',
    };
    return (
      <Button
        {...props}
        onClick={action(() => {
          this.modalOpen = true;
        })}
      >
		Review Request
      </Button>
    );
  }

  handleFileDownload = async event => {
    event.preventDefault();
    event.stopPropagation();

    // retrieve temporary link to download file
    const url = await this.props.accessRequest.downloadRequestFile(this.props.studyId, this.props.accessRequest.uuid);

    // redirect to download file link
    FileSaver.saveAs(url, this.props.accessRequest.requestFile.split('/').pop());
  };

  renderReviewAccessRequestForm() {
	  
    const form = getReviewRequestAccessForm();
	
    // format the dates and get upper/lower limit for access request dates
    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${today.getMonth() + 1 < 10 ? `0${today.getMonth() + 1}` : today.getMonth() + 1}-${`0${today.getDate()}`.slice(-2)}`;
    const maxDate = `${today.getFullYear() + 1}-${today.getMonth() + 1 < 10 ? `0${today.getMonth() + 1}` : today.getMonth() + 1}-${`0${today.getDate()}`.slice(-2)}`;
    form.$('expiry').min = formattedDate;
    form.$('expiry').max = maxDate;

    // edited by Reagan - add option for archived study 	  
    // populate the form with requested details
    form.$('expiry').value = this.accessRequest.expiry;
    form.$('isRead').value = this.accessRequest.isRead;
    form.$('isUpload').value = this.accessRequest.isUpload;
    form.$('isMetaData').value = this.accessRequest.isMetaData;
    form.$('permissionType').value = this.accessRequest.permissionType
      ? this.accessRequest.permissionType
      : this.accessRequest.collectionIds
      ? 'collection'
      : 'study';
    if (this.accessRequest.selectedRetrieval) form.$(`selectedRetrieval`).value = this.accessRequest.selectedRetrieval;	  
    form.$('collectionIds').value = this.accessRequest.collectionIds ? this.accessRequest.collectionIds : []; // for historical requests that did not have this property
    form.$('requestorComments').value = this.accessRequest.requestorComments ? this.accessRequest.requestorComments : '-'; // for historical requests that did not have this property
    if (this.accessRequest.uuid) form.$('uuid').value = this.accessRequest.uuid; // for newer requests that have this property
    if (this.accessRequest.requestFile) form.$('requestFile').value = this.accessRequest.requestFile; // for newer requests that have this property
    let permissionTypes = getOptionsFromRules(form.$('permissionType').rules);
    const retrievalTypes = getOptionsFromRules(form.$(`selectedRetrieval`).rules);	  
    if (this.props.collections.length === 0) {
      permissionTypes = permissionTypes.filter(type => type.value !== 'collection');
    }
	
    const approvalOptions = [{key: 'approve', text: 'Approve', value: 'approve'}, {key: 'deny', text: 'Deny', value: 'deny'}];

    return (
      <Form
        form={form}
        onCancel={this.handleFormCancel}
        onSuccess={this.handleFormSubmission}
        onAlternate={this.handleRevokePermission}
        onError={this.handleFormError}
      >
        {({ processing, onSubmit, onCancel }) => {
          return (
            <>
              <Header field={{id: 'study', label:'Study'}}/>
			  <>{this.props.studyId}</>
              <Header field={{id: 'requestor', label:'Requestor'}}/>
			  <ByWithProfile user={this.props.usersStore.asUserObjectByNsUsername(this.accessRequest.ns, this.accessRequest.username)}/>
              <hr />
			  
			  <DateField field={form.$('expiry')} />
			  <Toggle field={form.$('isRead')} disabled />
			  {!this.props.isSecure && form.$('permissionType').value === 'study' && (
				<Toggle field={form.$('isUpload')} />
			  )}
			  {!this.props.isSecure && form.$('permissionType').value === 'study' && (
				<Toggle field={form.$('isMetaData')} />
			  )}
		          {this.props.accessRequest.isArchived && <Radio radio field={form.$('selectedRetrieval')} options={retrievalTypes} />} 
			  <Radio radio field={form.$('permissionType')} options={permissionTypes} />
			  {form.$('permissionType').value === 'collection' && this.props.collections.length > 0 && (
				<StudyCollectionsCheckbox
				  field={form.$('collectionIds')}
				  options={this.props.collections.map(collection => ({
					key: collection.collectionId,
					text: collection.collectionName,
					value: collection.collectionId,
					description: collection.description,
				  }))}
				/>
			  )}
			  <TextArea field={form.$('requestorComments')} disabled />
			  {this.accessRequest.requestFile && (
				<div className="mb4">
				  <Button type="button" onClick={this.handleFileDownload}>
					Download attached file
				  </Button>
				  <br />
				</div>
			  )}
			  
			  <hr/>
			  <Header field={{id: 'action', label:'Action'}}/>
			  <Dropdown placeholder="Approve/Deny" options={approvalOptions} onChange={(e, { value }) => { runInAction(() => { this.decision = value; });}}/>
			  <br/>
              <TextArea field={form.$('adminComments')} />
              <Button
                onClick={onSubmit}
                className="ml2 mb3"
                floated="right"
                color="blue"
                icon
                disabled={processing}
                type="submit"
              >
                Submit
              </Button>
              <Button className="mb3" floated="right" disabled={processing} onClick={onCancel}>
                Cancel
              </Button>
            </>
          );
        }}
      </Form>
    );
  }
}

export default inject('userStore', 'usersStore')(observer(ReviewAccessRequest));
